import usePageTitle from '../hooks/usePageTitle';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, Loader2 } from 'lucide-react';

const RegisterPage = () => {
  usePageTitle('Register');
  const [step, setStep] = useState('form'); // form, success
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    contact_name: '',
    phone: '',
    address: '',
    abn: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await auth.register(registerData);
      
      if (response.data.pending_approval) {
        setStep('success');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-4">Registration Successful!</h1>
          <p className="text-[#64748B] mb-8">
            Your account has been created and is pending approval. Our team will review your application and you&apos;ll be notified once approved.
          </p>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-6 mb-8 text-left">
            <h3 className="font-semibold text-[#0F172A] mb-3">What happens next?</h3>
            <ol className="space-y-2 text-sm text-[#64748B]">
              <li>1. Our team will review your business details</li>
              <li>2. You&apos;ll receive an email once approved</li>
              <li>3. Log in to start placing wholesale orders</li>
            </ol>
          </div>
          <Link to="/login">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Create Wholesale Account</h1>
          <p className="text-[#64748B]">Register your business for wholesale access</p>
        </div>

        <div className="bg-[#FEF3C7] border border-[#F59E0B] p-4 mb-8">
          <p className="text-sm text-[#92400E]">
            <strong>Note:</strong> New accounts require admin approval before you can place orders. 
            You&apos;ll be notified via email once your account is approved.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium text-[#0F172A]">
                Company / Business Name *
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Your Company Pty Ltd"
                required
                className="h-12"
                data-testid="company-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="abn" className="text-sm font-medium text-[#0F172A]">
                ABN (Australian Business Number) *
              </Label>
              <Input
                id="abn"
                name="abn"
                value={formData.abn}
                onChange={handleChange}
                placeholder="XX XXX XXX XXX"
                required
                className="h-12"
                data-testid="abn-input"
              />
              <p className="text-xs text-[#64748B]">Your 11-digit Australian Business Number</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="contact_name" className="text-sm font-medium text-[#0F172A]">
                Contact Name *
              </Label>
              <Input
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="John Smith"
                required
                className="h-12"
                data-testid="contact-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-[#0F172A]">
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+61 4XX XXX XXX"
                required
                className="h-12"
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">
              Business Email *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@company.com"
              required
              className="h-12"
              data-testid="email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="text-sm font-medium text-[#0F172A]">
              Business Address *
            </Label>
            <Textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Business St, City, State, ZIP"
              rows={3}
              required
              data-testid="address-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#0F172A]">
                Password *
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="h-12 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#0F172A]">
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                minLength={6}
                className="h-12"
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#002FA7] hover:bg-[#0025a0] text-white font-semibold"
            data-testid="register-submit-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-[#64748B]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
